import React, { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';
import './ImageUpload.css';

const MAX_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const CROP_ASPECT = 3 / 4;

function ImageUpload({ currentUrl, onUploadComplete, initials }) {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const cropContainerRef = useRef(null);
  const canvasRef = useRef(null);

  const [localUrl, setLocalUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  // Crop state
  const [cropMode, setCropMode] = useState(false);
  const [rawFile, setRawFile] = useState(null);
  const [rawImageEl, setRawImageEl] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);
  const offsetAtDragStart = useRef(null);

  const preview = localUrl || currentUrl || null;

  // When a raw image loads into the crop UI, fit it to the crop box
  const initCropPosition = useCallback((img, containerW, containerH) => {
    const imgAspect = img.naturalWidth / img.naturalHeight;
    let s;
    if (imgAspect > CROP_ASPECT) {
      // image is wider — fit height
      s = containerH / img.naturalHeight;
    } else {
      // image is taller — fit width
      s = containerW / img.naturalWidth;
    }
    setScale(s);
    setOffset({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (rawImageEl && cropContainerRef.current) {
      const { offsetWidth: w, offsetHeight: h } = cropContainerRef.current;
      initCropPosition(rawImageEl, w, h);
    }
  }, [rawImageEl, initCropPosition]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Please upload a JPG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setError('Image must be under 5MB.');
      return;
    }
    if (!user) {
      setError('You must be logged in to upload an image.');
      return;
    }

    // Load image to get natural dimensions
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setRawImageEl(img);
      setRawFile(file);
      setCropMode(true);
    };
    img.src = objectUrl;

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Drag handlers ──────────────────────────────────────
  const getEventPos = (e) => {
    if (e.touches) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    setDragging(true);
    dragStart.current = getEventPos(e);
    offsetAtDragStart.current = { ...offset };
  };

  const handleDragMove = useCallback((e) => {
    if (!dragging || !dragStart.current) return;
    e.preventDefault();
    const pos = getEventPos(e);
    const dx = pos.x - dragStart.current.x;
    const dy = pos.y - dragStart.current.y;

    const container = cropContainerRef.current;
    if (!container || !rawImageEl) return;
    const cw = container.offsetWidth;
    const ch = container.offsetHeight;
    const iw = rawImageEl.naturalWidth * scale;
    const ih = rawImageEl.naturalHeight * scale;

    // Clamp so image always covers the crop box
    const minX = Math.min(0, cw - iw);
    const minY = Math.min(0, ch - ih);

    const newX = Math.max(minX, Math.min(0, offsetAtDragStart.current.x + dx));
    const newY = Math.max(minY, Math.min(0, offsetAtDragStart.current.y + dy));

    setOffset({ x: newX, y: newY });
  }, [dragging, rawImageEl, scale]);

  const handleDragEnd = () => {
    setDragging(false);
    dragStart.current = null;
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchmove', handleDragMove, { passive: false });
    window.addEventListener('touchend', handleDragEnd);
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [handleDragMove]);

  // ── Canvas crop + upload ──────────────────────────────
  const handleSavePhoto = async () => {
    if (!rawImageEl || !canvasRef.current || !rawFile) return;

    const container = cropContainerRef.current;
    const cw = container.offsetWidth;
    const ch = container.offsetHeight;

    // Work out what portion of the natural image is visible
    const srcX = (-offset.x) / scale;
    const srcY = (-offset.y) / scale;
    const srcW = cw / scale;
    const srcH = ch / scale;

    // Render at 2x for retina
    const outW = cw * 2;
    const outH = ch * 2;
    const canvas = canvasRef.current;
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(rawImageEl, srcX, srcY, srcW, srcH, 0, 0, outW, outH);

    setUploading(true);
    setCropMode(false);

    canvas.toBlob(async (blob) => {
      if (!blob) {
        setError('Could not process image. Please try again.');
        setUploading(false);
        return;
      }

      try {
        const ext = 'jpg';
        const filePath = `${user.id}/avatar.${ext}`;

        const uploadPromise = supabase.storage
          .from('avatars')
          .upload(filePath, blob, { upsert: true, contentType: 'image/jpeg' });

        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Upload timeout')), 15000)
        );

        const { error: uploadErr } = await Promise.race([uploadPromise, timeout]);
        if (uploadErr) throw uploadErr;

        const { data: urlData } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        const publicUrl = urlData?.publicUrl;
        if (!publicUrl) throw new Error('Could not get public URL.');

        const finalUrl = `${publicUrl}?t=${Date.now()}`;
        setLocalUrl(finalUrl);
        onUploadComplete(finalUrl);

      } catch (err) {
        setError('Upload failed. Please try again.');
        if (process.env.NODE_ENV === 'development') {
          console.error('ImageUpload error:', err.message);
        }
      } finally {
        setUploading(false);
      }
    }, 'image/jpeg', 0.92);
  };

  const handleCancelCrop = () => {
    setCropMode(false);
    setRawFile(null);
    setRawImageEl(null);
  };

  // ── Render ────────────────────────────────────────────
  if (cropMode && rawImageEl) {
    const iw = rawImageEl.naturalWidth * scale;
    const ih = rawImageEl.naturalHeight * scale;

    return (
      <div className="image-upload">
        <div className="image-crop-ui">
          <p className="image-crop-hint">Drag to reposition your photo</p>
          <div
            className="image-crop-box"
            ref={cropContainerRef}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            style={{ cursor: dragging ? 'grabbing' : 'grab' }}
          >
            <img
              src={rawImageEl.src}
              alt="Crop preview"
              className="image-crop-img"
              style={{
                width: iw,
                height: ih,
                transform: `translate(${offset.x}px, ${offset.y}px)`,
              }}
              draggable={false}
            />
          </div>
          <div className="image-crop-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSavePhoto}
            >
              Save Photo
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleCancelCrop}
            >
              Cancel
            </button>
          </div>
        </div>
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    );
  }

  return (
    <div className="image-upload">
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <div className="image-upload-preview-wrap">
        {uploading ? (
          <div className="image-upload-placeholder">
            <div className="image-upload-spinner" />
          </div>
        ) : preview ? (
          <img src={preview} alt="Profile" className="image-upload-preview" />
        ) : (
          <div className="image-upload-placeholder">
            {initials || '?'}
          </div>
        )}
        {uploading && <div className="image-upload-overlay">Uploading…</div>}
      </div>

      <div className="image-upload-controls">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          id="avatar-upload"
          className="image-upload-input"
          onChange={handleFileChange}
          disabled={uploading}
          aria-label="Upload profile photo"
        />
        <label
          htmlFor="avatar-upload"
          className={`btn btn-ghost image-upload-btn${uploading ? ' disabled' : ''}`}
        >
          {uploading ? 'Uploading…' : 'Change Photo'}
        </label>
        <p className="image-upload-hint">
          JPG, PNG, or WebP · Max 5MB · Drag to position after selecting
        </p>
      </div>

      {error && <p className="form-error">{error}</p>}
    </div>
  );
}

export default ImageUpload;
